import React from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import useStableSearchParams from '@/hooks/useStableSearchParams';

/**
 * Compact aggregation selector rendered as a single button with a dropdown menu.
 * Reads/writes selection to the `agg` search param (day|week|month).
 */
const AggregationSelector = ({ param = 'agg' }) => {
  const [searchParams, setSearchParams] = useStableSearchParams();
  const current = searchParams.get(param) || 'day';

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const setAgg = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(param, value);
      else next.delete(param);
      return next;
    });
    closeMenu();
  };

  const labelMap = { day: 'Dag', week: 'Uge', month: 'Måned' };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={openMenu}
        endIcon={<ArrowDropDownIcon />}
        data-testid="agg-button"
        sx={{
          borderRadius: 2,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          textTransform: 'none',
          p: 0.9,
          color: 'text.primary',
        }}
      >
        {labelMap[current] ?? labelMap.week}
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem
          selected={current === 'day'}
          onClick={() => setAgg('day')}
          data-testid="agg-day"
        >
          Dag
        </MenuItem>
        <MenuItem
          selected={current === 'week'}
          onClick={() => setAgg('week')}
          data-testid="agg-week"
        >
          Uge
        </MenuItem>
        <MenuItem
          selected={current === 'month'}
          onClick={() => setAgg('month')}
          data-testid="agg-month"
        >
          Måned
        </MenuItem>
      </Menu>
    </>
  );
};

export default AggregationSelector;
